import React, { FC, useCallback, useEffect, useState } from 'react';
import { WebUxPage, WebUxPageContent, WebUxPageItem } from '../webux/webux-layouts/WebUxPage';
import { Button, Modal, Space } from 'antd';
import { QueryBuilder } from '../../components/molecules/QueryBuilder/QueryBuilder';
import { DataTable, DataTablePagination } from '../../components/molecules/DataTable/DataTable';
import { useConnection } from '../../connection/Application';
import { InfoView, InfoViewItem } from '../../components/info-view/InfoView';
import { ExclamationCircleOutlined, PlusOutlined } from '@ant-design/icons';
import { FlexBox } from '../../components/atoms/FlexBox';
import { FormModal, useFormModal } from '../../components/form-modal/FormModal';
import { EmailTemplateData } from '../../entity/EmailTemplates';
import Form from '../../form/Form';
import './emailTemplate.less';
import HtmlTemplateView from '../../components/HtmlTemplate/HtmlTemplateView';
import { PageHeader } from '@ant-design/pro-layout';

const columns = [
  {
    title: 'Id',
    dataIndex: 'id',
    key: 'id',
  },
  {
    title: 'Name',
    dataIndex: 'name',
    key: 'name',
  },
];

const schema = [
  {
    id: 'name',
    name: 'Name',
    type: 'string',
    options: {
      rules: [{ required: true, whitespace: true, message: 'Name required' }],
    },
  },
  {
    id: 'emailTemplate',
    name: 'Email Template',
    type: 'template',
  },
];

const EmailTemplate: FC = () => {
  const [visible, setVisible] = useState<boolean>(false);
  const [filter, setFilter] = useState<any>({});
  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState<EmailTemplateData[]>();
  const [total, setTotal] = useState<number>(0);
  const [current, setCurrent] = useState<EmailTemplateData>();
  const [pagination, setPagination] = useState<DataTablePagination>({ page: 1, size: 10 });
  const connection = useConnection();
  const createForm = useFormModal<EmailTemplateData>();
  const editForm = useFormModal<EmailTemplateData>();
  // const emailEditorRef = useRef(null);

  const getTemplate = useCallback(
    (filter: any, pagination: DataTablePagination) => {
      setLoading(true);
      connection
        .get('email/email-template', { ...pagination, ...filter })
        .then((response: { total: number; emailTemplate: EmailTemplateData[] }) => {
          const responseData = response.emailTemplate.map((item) => {
            item.emailTemplate = JSON.parse(item.template as any);
            return { ...item, key: item.id };
          });
          setData(responseData);
          setTotal(response.total);
        })
        .finally(() => setLoading(false));
    },
    [connection],
  );

  useEffect(() => {
    getTemplate({}, { page: 1, size: 10 });
  }, [connection, getTemplate]);

  const addTemplate = useCallback(
    async (emailTemplate: EmailTemplateData) => {
      try {
        emailTemplate.template = JSON.stringify(emailTemplate.emailTemplate);
        delete emailTemplate.emailTemplate;

        await connection.post('email/email-template', emailTemplate);
        createForm.close();
        getTemplate(filter, pagination);
      } catch (e: any) {
        if (e.body?.body?.errors) {
          createForm.setErrors(e.body.body.errors);
          console.log(e);
        } else {
          createForm.setErrors([]);
        }
      }
    },
    [connection, createForm, filter, getTemplate, pagination],
  );

  const editTemplate = useCallback(
    async (emailTemplate: EmailTemplateData) => {
      try {
        const emailTemplateId = current && current.id;
        emailTemplate.id = emailTemplateId;
        emailTemplate.template = JSON.stringify(emailTemplate.emailTemplate);
        delete emailTemplate.emailTemplate;

        await connection.patch(`email/email-template/${emailTemplateId}`, emailTemplate);
        editForm.close();
        setVisible(false);
        getTemplate(filter, pagination);
      } catch (e: any) {
        if (e.body?.body?.errors) {
          editForm.setErrors(e.body.body.errors);
        } else {
          editForm.setErrors([]);
        }
      }
    },
    [connection, current, editForm, filter, getTemplate, pagination],
  );

  const search = useCallback(
    (filter: any, pagination: DataTablePagination) => {
      setLoading(true);
      connection
        .get('email/email-template', { ...pagination, ...filter })
        .then((response: { total: number; emailTemplate: EmailTemplateData[] }) => {
          const responseData = response.emailTemplate.map((item) => {
            item.emailTemplate = JSON.parse(item.template as any);
            return item;
          });
          setData(responseData);
          setTotal(response.total);
        })
        .finally(() => setLoading(false));
    },
    [connection],
  );

  const deleteTemplate = useCallback(() => {
    const emailTemplateId = current && current.id;
    Modal.confirm({
      title: 'Delete Email Template',
      icon: <ExclamationCircleOutlined />,
      content: 'Are you sure delete to this Email Template?',
      okText: 'Delete',
      okType: 'danger',
      onOk() {
        connection.delete(`email/email-template/${emailTemplateId}`).then(() => {
          setVisible(false);
          getTemplate(filter, pagination);
        });
      },
      onCancel() {
        console.log('Cancel');
      },
    });
  }, [connection, current, filter, getTemplate, pagination]);

  return (
    <WebUxPage>
      <PageHeader
        ghost={false}
        style={{ background: '#FFF' }}
        title="Email Template"
        subTitle="Email Template Management"
      />
      <WebUxPageContent>
        <WebUxPageItem
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <QueryBuilder
            searchPlaceholder="Search Email Template"
            search={true}
            value={filter}
            onChange={setFilter}
            onSearch={(value) => {
              setPagination({ page: 1, size: 10 });
              search(value, pagination);
            }}
            schema={[]}
          />
          <Space>
            <Button
              onClick={() => {
                setFilter({});
                setPagination({ page: 1, size: 10 });
                search({}, { page: 1, size: 10 });
              }}>
              Reset
            </Button>
            <Button type="primary">Search</Button>
          </Space>
        </WebUxPageItem>
        <WebUxPageItem>
          <Space direction={'vertical'} style={{ width: '100%' }} size={16}>
            <FlexBox horizontalAlign={'right'}>
              <Button
                icon={<PlusOutlined />}
                key="1"
                type="primary"
                onClick={() => createForm.open()}>
                New Email Template
              </Button>
            </FlexBox>
            <DataTable
              columns={columns}
              total={total}
              loading={loading}
              dataSource={data}
              onPaginationChange={(p) => {
                setPagination(p);
                if (pagination.page !== p.page || pagination.size !== p.size) {
                  search(filter, p);
                }
              }}
              onClick={(item: EmailTemplateData) => {
                setVisible(true);
                setCurrent(item);
              }}
              dtPagination={pagination}
            />
          </Space>
        </WebUxPageItem>
      </WebUxPageContent>
      <Modal
        open={visible}
        title={'Email Template'}
        onCancel={() => {
          setVisible(false);
        }}
        footer={
          <Space align={'end'}>
            <Button
              onClick={() => {
                setVisible(false);
              }}>
              Close
            </Button>
            <Button onClick={() => editForm.open(current)}>Edit</Button>
            <Button type="primary" onClick={deleteTemplate}>
              Delete
            </Button>
          </Space>
        }>
        <InfoView>
          <InfoViewItem title={'Name'}>{current?.name}</InfoViewItem>
        </InfoView>
        <HtmlTemplateView value={current?.emailTemplate?.html as any} />
      </Modal>
      <FormModal title={'Add Email Template'} onSubmit={addTemplate} form={createForm}>
        <Form.Items schema={schema} />
      </FormModal>
      <FormModal title={'Edit Email Template'} onSubmit={editTemplate} form={editForm}>
        <Form.Items schema={schema} />
      </FormModal>
    </WebUxPage>
  );
};
export default EmailTemplate;
